import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  SearchFilters,
  CourseSearchResult,
  CourseDetailResponse,
  searchCourses as apiSearchCourses,
  getCourseDetail as apiGetCourseDetail,
} from '../api/courses';

interface CoursesState {
  searchResults: CourseSearchResult[];
  selectedCourse: CourseDetailResponse | null;
  searchFilters: SearchFilters;
  nextToken?: string;
  loading: boolean;
  error: string | null;
}

const initialState: CoursesState = {
  searchResults: [],
  selectedCourse: null,
  searchFilters: {},
  nextToken: undefined,
  loading: false,
  error: null,
};

export const fetchSearchResults = createAsyncThunk(
  'courses/search',
  async (filters: SearchFilters, { rejectWithValue }) => {
    try {
      return await apiSearchCourses(filters);
    } catch (error: any) {
      return rejectWithValue(error.message as string);
    }
  },
);

export const fetchCourseDetail = createAsyncThunk(
  'courses/detail',
  async (courseId: string, { rejectWithValue }) => {
    try {
      return await apiGetCourseDetail(courseId);
    } catch (error: any) {
      return rejectWithValue(error.message as string);
    }
  },
);

const coursesSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    setSearchFilters: (state, action: PayloadAction<SearchFilters>) => {
      state.searchFilters = action.payload;
    },
    clearSearch: (state) => {
      state.searchResults = [];
      state.nextToken = undefined;
      state.error = null;
    },
    clearSelectedCourse: (state) => {
      state.selectedCourse = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSearchResults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSearchResults.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload.items;
        state.nextToken = action.payload.nextToken;
      })
      .addCase(fetchSearchResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCourseDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCourseDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCourse = action.payload;
      })
      .addCase(fetchCourseDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSearchFilters, clearSearch, clearSelectedCourse } = coursesSlice.actions;
export default coursesSlice.reducer;
